import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  CONSENT_OPEN_EVENT, applyConsent, clearGaCookies, getBrowserStorage, readConsent, revokeConsent, saveConsent, shouldAsk,
  type ConsentChoice,
} from "@/lib/cookie-consent";

/** Aviso de cookies de analítica (Google Analytics 4, Consent Mode v2; ver lib/cookie-consent.ts).
 *
 *  - Solo existe en el navegador: arranca oculto y decide al montar (`useEffect`), así el HTML
 *    prerenderizado no lo incluye ni como contenido indexable. El prerender además marca
 *    `window.__CP_PRERENDER__` (scripts/prerender-content.mjs) y se verifica que no aparezca.
 *  - Posición fija inferior: no empuja contenido (sin CLS). En móvil ocupa todo el ancho pegado al
 *    borde inferior (cubre el botón de WhatsApp mientras no se elija, z-index mayor); en pantallas
 *    anchas va abajo a la izquierda.
 *  - «Aceptar» y «Rechazar» comparten estilo exacto (misma jerarquía visual).
 *  - `theme="kidstenia"`: misma lógica y mismo texto, con la paleta propia de /programa-kidstenia
 *    (blanco, navy #14213D, cyan #2CC6F4; Baloo 2 + Montserrat). No se imprime (`print:hidden`). */
export function CookieConsentBanner({ theme = "cp" }: { theme?: "cp" | "kidstenia" } = {}) {
  const kids = theme === "kidstenia";
  const [open, setOpen] = useState(false);
  const firstButton = useRef<HTMLButtonElement>(null);
  const reopened = useRef(false);

  useEffect(() => {
    if ((window as unknown as { __CP_PRERENDER__?: boolean }).__CP_PRERENDER__) return;
    if (shouldAsk(getBrowserStorage())) setOpen(true);
    const onOpen = () => { reopened.current = true; setOpen(true); };
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
  }, []);

  // Si lo reabrió la persona desde el pie, se lleva el foco al primer botón (teclado y lector de pantalla).
  useEffect(() => { if (open && reopened.current) firstButton.current?.focus(); }, [open]);

  if (!open) return null;

  const choose = (choice: ConsentChoice) => {
    const storage = getBrowserStorage();
    saveConsent(storage, choice);
    applyConsent((window as unknown as { gtag?: (...a: unknown[]) => void }).gtag, choice);
    if (choice === "denied") clearGaCookies();
    reopened.current = false;
    setOpen(false);
  };
  const current = readConsent(getBrowserStorage());

  const kidsFont = { fontFamily: "'Montserrat',sans-serif" };
  const kidsBtn: React.CSSProperties = { fontFamily: "'Baloo 2',sans-serif", fontWeight: 800, fontSize: 14, textTransform: "uppercase", color: "#14213D", background: "#FFFFFF", border: "2px solid #2CC6F4", borderRadius: 999, padding: "10px 12px", cursor: "pointer" };
  const onRevoke = () => { revokeConsent(getBrowserStorage()); applyConsent((window as unknown as { gtag?: (...a: unknown[]) => void }).gtag, "denied"); clearGaCookies(); reopened.current = false; setOpen(false); };

  return (
    <section
      data-cp-cookie-banner
      role="region"
      aria-label="Preferencias de cookies"
      className={`print:hidden fixed z-[70] left-0 right-0 bottom-0 pb-[calc(1.25rem+env(safe-area-inset-bottom))] md:pb-5 md:left-5 md:right-auto md:bottom-5 md:max-w-md p-5 ${kids ? "md:rounded-2xl border-2 border-[#2CC6F4] bg-white text-[#14213D] shadow-[0_8px_32px_rgba(20,33,61,0.25)]" : "border border-[var(--cp-line)] bg-black text-white shadow-[0_8px_32px_rgba(0,0,0,0.5)]"}`}
      style={kids ? kidsFont : undefined}
    >
      <p className={kids ? "text-[11px] font-bold uppercase tracking-[0.15em] text-[#14213D]/70 mb-3" : "font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--cp-cyan)] mb-3"}>COOKIES</p>
      <p className={kids ? "text-sm leading-relaxed text-[#14213D]/80" : "text-sm leading-relaxed text-white/70"}>
        Usamos Google Analytics para medir de forma agregada cómo se usa el sitio. Solo se activa si aceptas; puedes cambiarlo cuando quieras desde «Preferencias de cookies» al final de la página. Más detalle en la{" "}
        <Link to="/privacidad" className={kids ? "font-semibold text-[#14213D] underline underline-offset-2" : "text-[var(--cp-cyan)] underline underline-offset-2"}>Política de Privacidad</Link>.
      </p>
      {current && (
        <p className={kids ? "mt-2 text-xs text-[#14213D]/60" : "mt-2 text-xs text-white/50"}>Elección actual: {current.choice === "granted" ? "aceptada" : "rechazada"}.</p>
      )}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <button ref={firstButton} type="button" onClick={() => choose("denied")} className={kids ? undefined : "cp-btn-ghost !px-3 !py-3 !text-[0.7rem]"} style={kids ? kidsBtn : undefined}>Rechazar</button>
        <button type="button" onClick={() => choose("granted")} className={kids ? undefined : "cp-btn-ghost !px-3 !py-3 !text-[0.7rem]"} style={kids ? kidsBtn : undefined}>Aceptar</button>
      </div>
      {current && (
        <button type="button" onClick={onRevoke} className={kids ? "mt-3 text-xs font-semibold text-[#14213D]/70 underline" : "mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-[var(--cp-cyan)] transition"}>
          Borrar mi elección
        </button>
      )}
    </section>
  );
}
